import { serverDate } from '../../../shared/helpers/dates';
import { LocalizedDatePipe } from '../../../shared/pipe/localized-date.pipe';
import { CommonModule } from '@angular/common';
import {
  Component,
  DestroyRef,
  OnInit,
  computed,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule, NonNullableFormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { RouterModule } from '@angular/router';

import { ModalComponent } from '../../../shared/components/ui/modal/modal.component';
import { DataStateComponent } from '../../../shared/components/ui/data-state/data-state.component';
import { PageHeaderComponent } from '../../../shared/components/ui/page-header/page-header.component';
import { UserCellComponent } from '../../../shared/components/ui/user-cell/user-cell.component';
import { PAGE_SIZE_OPTIONS } from '../../../shared/components/ui/pretty-select/common-options';
import {
  PrettySelectComponent,
  PrettySelectOption,
} from '../../../shared/components/ui/pretty-select/pretty-select.component';
import { toErrorMessage } from '../../../shared/helpers/http-error';
import { KycService } from '../../../shared/services/kyc.service';
import {
  KYC_DOCUMENT_TYPE_LABEL,
  KYC_STATUS_META,
  KYC_SUBJECT_TYPE_LABEL,
  KycDocument,
  KycDocumentStatus,
  KycSubjectType,
} from '../../../shared/types/kyc.types';

/** Fenêtre de pré-alerte, alignée sur le cron back (`REMINDER_DAY_MARKS` = 30/15/7 j). */
const EXPIRY_SOON_DAYS = 30;

const STATUS_OPTIONS: PrettySelectOption<KycDocumentStatus>[] = [
  { value: 'PENDING', label: 'En attente' },
  { value: 'VALIDATED', label: 'Validé' },
  { value: 'EXPIRED', label: 'Expiré' },
  { value: 'REJECTED', label: 'Rejeté' },
];

const SUBJECT_OPTIONS: PrettySelectOption<KycSubjectType>[] = [
  { value: 'OPERATOR', label: 'Opérateur' },
  { value: 'OWNER', label: 'Bailleur' },
  { value: 'DRIVER', label: 'Chauffeur' },
];

/**
 * « Pièces KYC » (#93) — supervision du parc documentaire, **tous acteurs**
 * (`GET /operator/api/kyc`, tri back `expiryDate` ASC nulls last).
 *
 * Distinct de `/operator-kyc` (décider d'un **dossier**) et de `/operators/{id}#kyc`
 * (traiter **un** opérateur) : ici on voit le stock transverse, et surtout les pièces
 * **EXPIRED** à renouveler — invisibles ailleurs en vue globale.
 *
 * Valider une pièce ≠ valider un dossier : ça arme le cron d'expiration
 * (rappels J-30/J-15/J-7, qui ne balaye que les documents `VALIDATED`) et ne
 * débloque **pas** le recrutement. Le rejet d'une pièce **ne notifie pas**.
 */
@Component({
  selector: 'app-kyc-queue',
  imports: [
    CommonModule,
    LocalizedDatePipe,
    ReactiveFormsModule,
    FormsModule,
    RouterModule,
    PageHeaderComponent,
    DataStateComponent,
    ModalComponent,
    PrettySelectComponent,
    UserCellComponent,
  ],
  templateUrl: './kyc-queue.component.html',
})
export class KycQueueComponent implements OnInit {
  private readonly service = inject(KycService);
  private readonly fb = inject(NonNullableFormBuilder);
  private readonly destroyRef = inject(DestroyRef);

  readonly loading = signal(false);
  readonly error = signal<string | null>(null);
  readonly items = signal<KycDocument[]>([]);
  readonly totalItems = signal(0);
  readonly totalPages = signal(0);
  readonly page = signal(1);
  readonly size = signal(20);

  readonly pageSizeOptions = PAGE_SIZE_OPTIONS;
  readonly statusMeta = KYC_STATUS_META;
  readonly docTypeLabel = KYC_DOCUMENT_TYPE_LABEL;
  readonly subjectLabel = KYC_SUBJECT_TYPE_LABEL;

  // ── Filtres (#93) ─────────────────────────────────────────────────────
  // Défaut PENDING = le backlog à traiter (comportement historique de l'écran).
  readonly statusOptions = STATUS_OPTIONS;
  readonly subjectOptions = SUBJECT_OPTIONS;
  readonly statusFilter = signal<KycDocumentStatus | null>('PENDING');
  readonly subjectFilter = signal<KycSubjectType | null>(null);

  // ── Validate flow ─────────────────────────────────────────────────────
  readonly validatingId = signal<number | null>(null);

  // ── Reject modal ──────────────────────────────────────────────────────
  readonly rejectOpen = signal(false);
  readonly rejectTarget = signal<KycDocument | null>(null);
  readonly rejecting = signal(false);
  readonly rejectError = signal<string | null>(null);
  readonly rejectForm = this.fb.group({
    reason: ['', [Validators.required, Validators.minLength(3), Validators.maxLength(500)]],
  });

  readonly pageWindow = computed<number[]>(() => {
    const total = this.totalPages();
    if (total <= 1) return total === 1 ? [1] : [];
    const current = this.page();
    const window = 5;
    let start = Math.max(1, current - Math.floor(window / 2));
    const end = Math.min(total, start + window - 1);
    start = Math.max(1, end - window + 1);
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  });

  ngOnInit(): void { this.load(); }

  load(): void {
    this.loading.set(true);
    this.error.set(null);
    this.service
      .list(this.page(), this.size(), this.statusFilter(), this.subjectFilter())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.items.set(res.data ?? []);
          this.totalItems.set(res.totalItems ?? 0);
          this.totalPages.set(res.totalPages ?? 0);
          this.loading.set(false);
        },
        error: (err: unknown) => {
          this.loading.set(false);
          this.items.set([]);
          this.totalItems.set(0);
          this.totalPages.set(0);
          this.error.set(toErrorMessage(err));
        },
      });
  }

  goToPage(p: number): void {
    if (p < 1 || p > this.totalPages() || p === this.page()) return;
    this.page.set(p);
    this.load();
  }

  changeSize(s: number): void {
    if (!s || s === this.size()) return;
    this.size.set(s);
    this.page.set(1);
    this.load();
  }

  changeStatus(s: KycDocumentStatus | null): void {
    if (s === this.statusFilter()) return;
    this.statusFilter.set(s);
    this.page.set(1);
    this.load();
  }

  changeSubject(s: KycSubjectType | null): void {
    if (s === this.subjectFilter()) return;
    this.subjectFilter.set(s);
    this.page.set(1);
    this.load();
  }

  /** Nom lisible du sujet — `subjectName` du back, sinon repli sur le type + id. */
  subjectDisplay(doc: KycDocument): string {
    if (doc.subjectName) return doc.subjectName;
    return `${this.subjectLabel[doc.subjectType] || doc.subjectType} #${doc.subjectRef}`;
  }

  /**
   * État d'expiration d'une pièce, pour teinter la colonne Expiration — c'est
   * la raison d'être de cet écran.
   *
   * ⚠️ Ne se déduit pas du seul `kycStatus` : le passage à `EXPIRED` est fait par
   * le cron back, et il ne surveille que les documents `VALIDATED`. Un document
   * `PENDING` dont la date est passée reste donc `PENDING` — d'où le calcul sur
   * la date, pas sur le statut.
   */
  expiryState(doc: KycDocument): 'expired' | 'soon' | 'ok' | null {
    if (!doc.expiryDate) return null;
    const ms = serverDate(doc.expiryDate).getTime();
    if (Number.isNaN(ms)) return null;
    const days = Math.floor((ms - Date.now()) / 86_400_000);
    if (days < 0) return 'expired';
    return days <= EXPIRY_SOON_DAYS ? 'soon' : 'ok';
  }

  expiryHint(doc: KycDocument): string | null {
    const state = this.expiryState(doc);
    if (state === 'expired') return 'Expiré';
    if (state !== 'soon') return null;
    const days = Math.floor((serverDate(doc.expiryDate!).getTime() - Date.now()) / 86_400_000);
    return days === 0 ? "Expire aujourd'hui" : `Expire dans ${days} j`;
  }

  validate(doc: KycDocument): void {
    if (this.validatingId() !== null) return;
    this.validatingId.set(doc.id);
    this.service
      .validate(doc.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (updated) => {
          this.validatingId.set(null);
          this.applyDecision(updated);
        },
        error: (err: unknown) => {
          this.validatingId.set(null);
          this.error.set(toErrorMessage(err, 'La validation a échoué.'));
        },
      });
  }

  /**
   * Après décision : la ligne ne quitte la liste que si elle **sort du filtre
   * actif** (ex. filtre PENDING + validation). Sans filtre — ou si le nouveau
   * statut correspond au filtre — on patche en place, sinon la ligne
   * disparaîtrait à tort d'une liste où elle a toujours sa place.
   *
   * ⚠️ **Fusion, pas remplacement** : `validate`/`reject` renvoient le
   * `KycDocument` **brut** (sans `subjectName`/`user`, portés par le
   * `KycDocumentView` du listing). Un `set(updated)` ferait retomber la ligne
   * sur « Opérateur #7 » juste après la décision.
   */
  private applyDecision(updated: KycDocument): void {
    const filter = this.statusFilter();
    if (filter && updated.kycStatus !== filter) {
      this.items.set(this.items().filter((d) => d.id !== updated.id));
      this.totalItems.set(Math.max(0, this.totalItems() - 1));
      return;
    }
    this.items.set(
      this.items().map((d) => (d.id === updated.id ? { ...d, ...updated } : d)),
    );
  }

  // ── Reject ───────────────────────────────────────────────────────────
  openReject(doc: KycDocument): void {
    this.rejectTarget.set(doc);
    this.rejectForm.reset({ reason: '' });
    this.rejectError.set(null);
    this.rejectOpen.set(true);
  }
  closeReject(): void {
    if (this.rejecting()) return;
    this.rejectOpen.set(false);
    this.rejectTarget.set(null);
  }
  submitReject(): void {
    const target = this.rejectTarget();
    if (!target || this.rejectForm.invalid || this.rejecting()) {
      this.rejectForm.markAllAsTouched();
      return;
    }
    this.rejecting.set(true);
    this.rejectError.set(null);
    this.service
      .reject(target.id, { reason: this.rejectForm.controls.reason.value.trim() })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (updated) => {
          this.rejecting.set(false);
          this.rejectOpen.set(false);
          this.applyDecision(updated);
          this.rejectTarget.set(null);
        },
        error: (err: unknown) => {
          this.rejecting.set(false);
          this.rejectError.set(toErrorMessage(err, 'Le rejet a échoué.'));
        },
      });
  }
}
